<template>
	<div :class="['timelineEventItem flex-center flex w-full overflow-hidden', { 'bg-black': !isSpecialEventTemplate }]">
		<component
			:is="eventComponent"
			v-if="eventComponent"
			:event="rawEvent"
			:readonly="readonly"
			:simplified="simplified"
			:is-selected="isEventSelected"
		/>
	</div>
</template>

<script setup lang="ts">
import type { MbscCalendarEventData } from '@mobiscroll/vue'
import { storeToRefs } from 'pinia'
import { type Component, computed } from 'vue'
import { isSameDate } from '@/common/dateUtils'
import { isSpecialEventTemplate as isSpecialEventTemplateChecker } from '@/common/utils'
import { useEventDetailsStore } from '@/features/eventDetails/useEventDetailsStore'
import CooldownEvent from '@/features/events/components/timeline/events/CooldownEvent.vue'
import DiscountEvent from '@/features/events/components/timeline/events/DiscountEvent.vue'
import LocalDraftEvent from '@/features/events/components/timeline/events/LocalDraftEvent.vue'
import NoGoEvent from '@/features/events/components/timeline/events/NoGoEvent.vue'
import OpportunityEvent from '@/features/events/components/timeline/events/OpportunityEvent.vue'
import SpecialEventTemplate from '@/features/events/components/timeline/events/SpecialEventTemplate.vue'
import StoreOpportunityEvent from '@/features/events/components/timeline/events/StoreOpportunityEvent.vue'
import { EventType, type TimelineEvent } from '@/types'

const props = defineProps<{
	event: MbscCalendarEventData
	simplified?: boolean
	readonly?: boolean
}>()
const { products, startDate, endDate } = storeToRefs(useEventDetailsStore())
const rawEvent = computed(() => (props.event.original as TimelineEvent).rawEvent)
const isSpecialEventTemplate = computed(() => isSpecialEventTemplateChecker(rawEvent.value))

const isEventSelected = computed<boolean>(() => {
	return (
		!!startDate.value &&
		isSameDate(rawEvent.value.start_date, startDate.value) &&
		!!endDate.value &&
		isSameDate(rawEvent.value.end_date, endDate.value) &&
		products.value.some((el) => el.uniqueSkuId === rawEvent.value.unique_sku_id)
	)
})

const eventComponent = computed<Component | undefined>(() => {
	if (rawEvent.value.event_type === EventType.Cooldown) {
		return rawEvent.value.cooldown_direction === 'forward' ? CooldownEvent : NoGoEvent
	}

	if (rawEvent.value.event_type === EventType.StoreOpportunity && !rawEvent.value.opportunity_type) {
		// Actual discounts added in portal which are based on store events
		return DiscountEvent
	}

	if (isSpecialEventTemplate.value) {
		return SpecialEventTemplate
	}

	return {
		[EventType.StoreOpportunity]: StoreOpportunityEvent,
		[EventType.Cooldown]: CooldownEvent,
		[EventType.Discount]: DiscountEvent,
		[EventType.NoGo]: NoGoEvent,
		[EventType.Opportunity]: OpportunityEvent,
		[EventType.LocalDraft]: LocalDraftEvent
	}[rawEvent.value.event_type]
})
</script>

<style lang="scss" scoped>
.timelineEventItem {
	height: calc(var(--timeline-cell-height) + 0.125rem); // Account for 2px border (top and bottom)
}
</style>
